/* eslint-disable no-useless-escape */
/* eslint-disable @typescript-eslint/naming-convention */
import React, { FC, ReactNode, useEffect, useState } from 'react';

import { DIFF_SHOWFOR, DiffShowFor_Keys, random_key } from './RolloutDiffUtils';
import { SecondaryText } from '@/common/components';
import { REST_COUNTRIES_KEY } from '@/common/CountryCodesMap';
import Service, { Operators } from '@/lib/App';
import { Constraint, ConstraintType } from '@/lib/PlannerDTOs';

import './RolloutDiffUtils.css';

import { Flex } from '@duxton-web/ui';
import { TagType } from 'antd';
import _, { difference, round } from 'lodash';

interface RolloutDiffConstraintProps {
  baseConstraints: Constraint[];
  compareConstraints: Constraint[];
  showFor?: (typeof DIFF_SHOWFOR)[DiffShowFor_Keys];
  compact: boolean;
  constraintValue?: string;
}

const RolloutDiffConstraintV2: FC<RolloutDiffConstraintProps> = ({
  baseConstraints,
  compareConstraints,
  showFor = DIFF_SHOWFOR.ALL,
  compact,
  constraintValue = '',
}) => {
  const [facet_map, setFacetMap] = useState<{ [f: string]: string }>(undefined);
  const [cities_map, setCitiesMap] = useState<{ [c: string]: string }>(
    undefined,
  );
  const [operator_map, setOperatorMap] = useState<{ [op: string]: string }>(
    undefined,
  );
  const [vehicle_map, setVehicleMap] = useState<{ [v: string]: string }>(
    undefined,
  );

  const generate_array_to_map = (
    array: any,
    key: string,
    value: string,
    value1?: string,
  ): any => {
    return array.reduce((obj, item) => {
      let val = item[value];
      if (value1) {
        val = `${item[value]} - ${item[value1]}`;
      }
      obj[item[key]] = val;
      return obj;
    }, {});
  };

  useEffect(() => {
    setFacetMap(
      generate_array_to_map(Service.cache!.getAllFacets(), 'name', 'title'),
    );
    setCitiesMap(
      generate_array_to_map(Service.cache!.getAllCities(), 'id', 'name'),
    );
    setOperatorMap(Operators);
    setVehicleMap(
      generate_array_to_map(
        Service.cache!.getAllVehicles(),
        'id',
        'name',
        'id',
      ),
    );
  }, []);

  const formatColorArrayV2 = (
    values: string[] | undefined,
    defaultStyle: boolean,
    compareValues: string[] | undefined,
    target?: string,
    op?: string,
  ): React.ReactElement<TagType>[] | null => {
    if (!values) return;

    const items: React.ReactElement<TagType>[] | null = values.map((value) => {
      let className: string = `rollout-diff-tag-v2${
        compact ? ' rollout-diff-tag-v2-compact' : ''
      }`;

      if (!defaultStyle)
        className += `${className ? ' ' : ''}${
          showFor === DIFF_SHOWFOR.AFTER
            ? 'rollout-target-value-added'
            : DIFF_SHOWFOR.BEFORE
              ? 'rollout-target-value-deleted'
              : ''
        }`;

      if (compareValues && compareValues.includes(value)) {
        className = className.replace(
          showFor === DIFF_SHOWFOR.AFTER
            ? 'rollout-target-value-added'
            : DIFF_SHOWFOR.BEFORE
              ? 'rollout-target-value-deleted'
              : '',
          '',
        );
      }

      return (
        <div className={`rollout-diff-tag-v2 ${className || ''}`} key={value}>
          {render_value_label(target, op, value)}
        </div>
      );
    });

    let nullable_rows = items.length > 0 ? items : null;
    return nullable_rows;
  };

  const format_float = (f: string) => {
    const perc = round(parseFloat(f) * 100, 2);
    return `${perc}%`;
  };

  const format_comma_separated = (v: string) => {
    let value = v;
    if (v.startsWith('[') && v.endsWith(']')) {
      value = v.substring(1, v.length - 1);
    }
    return value.split(',');
  };

  const format_times = (t: string) => {
    return [t];
  };

  const format_cities = (c: any) => {
    if (cities_map) {
      return c.replaceAll(/[\[\]]/g, '').split(',');
    }

    return [];
  };

  const render_city_label = (city) => {
    if (cities_map && city) {
      return cities_map[city] || `${city} (deleted)`;
    }

    return '';
  };

  const format_countries = (c: any) => {
    if (Service.cache.getCountriesMap()) {
      return c.replaceAll(/[\[\]]/g, '').split(',');
    }

    return [];
  };

  const render_country_label = (country) => {
    const countriesMap = Service.cache.getCountriesMap();
    if (countriesMap && country) {
      return (
        countriesMap?.[country] ||
        countriesMap[REST_COUNTRIES_KEY]?.children?.[country] ||
        `${country} (deleted)`
      );
    }

    return '';
  };

  const format_vehicles = (c: any) => {
    if (vehicle_map) {
      return c.replaceAll(/[\[\]]/g, '').split(',');
    }

    return [];
  };

  const render_vehicle_label = (vehicle) => {
    if (vehicle_map && vehicle) {
      return vehicle_map[vehicle] || `${vehicle} (deleted)`;
    }

    return '';
  };

  const format_values = (c: Constraint): string[] => {
    if (!c) return null;

    const { target, op, value } = c;
    if (!value) {
      return null;
    }
    if (`${target},${op}` == 'pax,/') {
      return [format_float(value)];
    }
    switch (op) {
      case 'contains':
      case 'not contains':
      case 'ex':
        return [String(value)];
      case 'in':
      case 'not in':
        switch (target) {
          case 'city':
            return format_cities(value);
          case 'ctry':
            return format_countries(value);
          case 'svc':
            return format_vehicles(value);
          case 'ts':
            return format_times(value);
          default:
            return format_comma_separated(value);
        }
      default:
        return format_comma_separated(value);
    }
  };

  const render_value_label = (target, op, value): string => {
    if (!value) {
      return null;
    }

    switch (op) {
      case 'in':
      case 'not in':
        switch (target) {
          case 'city':
            return render_city_label(value);
          case 'ctry':
            return render_country_label(value);
          case 'svc':
            return render_vehicle_label(value);
          default:
            return value;
        }
      default:
        return value;
    }
  };

  function format_facet_v2(
    constraint: Constraint,
    defaultStyle: boolean,
  ): ReactNode {
    if (!facet_map) return false;

    let className = defaultStyle
      ? ''
      : showFor === DIFF_SHOWFOR.AFTER
        ? 'rollout-target-value-added'
        : 'rollout-target-value-deleted';

    if (compact)
      className += `${className ? ' ' : ''} rollout-diff-tag-v2-compact`;

    return (
      <div className={`rollout-diff-tag-v2 ${className || ''}`}>
        {constraint.type == ConstraintType.Standard ||
        constraint.type == ConstraintType.Targeting
          ? facet_map[constraint.target]
          : constraint.target}
      </div>
    );
  }

  function format_op_v2(
    constraint: Constraint,
    defaultStyle: boolean = true,
  ): ReactNode {
    if (!operator_map) return false;

    let className = defaultStyle
      ? ''
      : showFor === DIFF_SHOWFOR.AFTER
        ? 'rollout-target-value-added'
        : 'rollout-target-value-deleted';

    if (compact)
      className += `${className ? ' ' : ''} rollout-diff-tag-v2-compact`;

    const op = operator_map[constraint.op];

    return <div className={`rollout-diff-tag-v2 ${className || ''}`}>{op}</div>;
  }

  const compareConstraint = (baseConstraint: Constraint, compareConstraint) => {
    let values = format_values(baseConstraint);
    let compareValues = format_values(compareConstraint);
    const diff_values = difference(values, compareValues);

    return (
      <Flex gap="4px" wrap="wrap" align="center" key={random_key()}>
        {format_facet_v2(
          baseConstraint,
          baseConstraint?.target === compareConstraint?.target,
        )}
        {format_op_v2(
          baseConstraint,
          baseConstraint?.op === compareConstraint?.op,
        )}
        {formatColorArrayV2(
          values,
          diff_values.length === 0,
          compareValues,
          baseConstraint.target,
          baseConstraint.op,
        )}
      </Flex>
    );
  };

  const renderBaseConstraints = () => {
    if (!baseConstraints) return null;

    // filter out "targeted at" constraints, since these will be shown in the rollout percentage
    const filteredBaseConstraints = baseConstraints.filter(
      (constraint) => constraint?.op !== '/',
    );
    const filteredCompareConstraints = compareConstraints?.filter(
      (constraint) => constraint?.op !== '/',
    );

    const items = filteredBaseConstraints.map((baseConstraint: Constraint) => {
      if (!filteredCompareConstraints)
        return compareConstraint(baseConstraint, null);

      const constraintFound = filteredCompareConstraints.find(
        (compareConstraint) => {
          return baseConstraint?.target === compareConstraint?.target;
        },
      );

      if (!constraintFound) return compareConstraint(baseConstraint, null);

      return compareConstraint(baseConstraint, constraintFound);
    });

    return (
      <Flex vertical gap="4px">
        {items.map((item, index: number) => {
          return (
            <Flex align="center" gap={4} key={`base-constraint-${index}`}>
              {(constraintValue || index > 0) && (
                <div
                  style={{
                    padding: '4px 8px',
                    height: '32px',
                    backgroundColor: index > 0 ? '#F5F5F5' : '#FFF',
                    borderRadius: '8px',
                    width: 'fit-content',
                    minWidth: 'fit-content',
                    alignSelf: 'baseline',
                  }}
                >
                  <SecondaryText
                    strong
                    style={{
                      color: '#3D3D3D',
                    }}
                  >
                    {index > 0 ? 'AND' : constraintValue}
                  </SecondaryText>
                </div>
              )}
              {item}
            </Flex>
          );
        })}
      </Flex>
    );
  };

  return renderBaseConstraints();
};

export default RolloutDiffConstraintV2;
